<template>
  <div class="settings--content">
    <form @submit.prevent="updateActiveAgentBot">
      <settings-section
        :title="$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE')"
        :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.DESC')"
      >
        <div class="medium-8 columns label-wrapper">
          <label>
            <SelectTabDropdown
              :buttons="getButtons"
              :options="botsOption"
              :button-type="defaultButtonType"
              :selected-item="selectedBot"
              :additional-action-bottom="
                $t('AGENT_BOTS.BOT_CONFIGURATION.ADD_BOT')
              "
              :placeholder="placeholderValue"
              @action="redirectToCreateBot"
              @select="handleSelectedBot"
              @changeButtonType="changeType"
            >
              <div class="no-assistant-wrapper">
                <p class="title">
                  {{ $t('AGENT_BOTS.BOT_CONFIGURATION.NO_RESULT_TITLE') }}
                </p>
                <p class="description">
                  {{ $t('AGENT_BOTS.BOT_CONFIGURATION.NO_RESULT_DESCRIPTION') }}
                </p>
                <div>
                  <div class="button" @click="redirectToCreateBot">
                    {{ $t('AGENT_BOTS.BOT_CONFIGURATION.ADD_BOT') }}
                  </div>
                </div>
              </div>
            </SelectTabDropdown>
          </label>
          <div v-if="isCallInbox" class="beta-notice">
            <VFlag flag="GB" class="margin-right-2 beta-notice-flag" />
            <div>
              <div class="beta-notice-title">
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.BETA_NOTICE') }}
              </div>
              <div class="beta-notice-desc">
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.BETA_DESC') }}
              </div>
            </div>
          </div>
          <div class="button-container label-wrapper__buttons">
            <woot-submit-button
              :button-text="$t('AGENT_BOTS.BOT_CONFIGURATION.SUBMIT')"
              :loading="uiFlags.isSettingAgentBot"
            />
            <woot-button
              type="button"
              :disabled="!selectedAgentBotId"
              :loading="uiFlags.isDisconnecting"
              variant="smooth"
              color-scheme="alert"
              class="button--disconnect"
              @click="disconnectBot"
            >
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECT') }}
            </woot-button>
          </div>
        </div>
      </settings-section>
    </form>
    <form v-if="isKB" @submit.prevent="updateFollowUp">
      <settings-section
        v-if="!isACallInbox"
        :title="$t('AGENT_BOTS.BOT_CONFIGURATION.FOLLOW_UP.TITLE')"
        :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.FOLLOW_UP.DESC')"
      >
        <div class="medium-8 columns">
          <div class="medium-6 padding-right-1">
            <select v-model="followUp.enabled" @change="followUpChanged()">
              <option :value="true">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.ENABLED'
                  )
                }}
              </option>
              <option :value="false">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.DISABLED'
                  )
                }}
              </option>
            </select>
          </div>
          <div v-if="followUp.enabled">
            <div class="row">
              <div class="columns medium-6 padding-right-1">
                <woot-input
                  v-model.number="followUp.mins"
                  type="number"
                  class="columns settings-item"
                  label="Mins"
                  placeholder="0"
                  @input="$v.followUp.mins.$touch"
                />
                <span
                  v-if="$v.followUp.mins.$error"
                  class="message follow-up-message"
                >
                  {{
                    $t('AGENT_BOTS.BOT_CONFIGURATION.FOLLOW_UP.VALID_DURATION')
                  }}
                </span>
              </div>
              <div class="columns medium-6 padding-left-1">
                <woot-input
                  v-model.number="followUp.secs"
                  type="number"
                  class="columns settings-item"
                  label="Seconds"
                  placeholder="0"
                  @input="$v.followUp.secs.$touch"
                />
                <span
                  v-if="$v.followUp.secs.$error"
                  class="message follow-up-message"
                >
                  {{
                    $t('AGENT_BOTS.BOT_CONFIGURATION.FOLLOW_UP.VALID_DURATION')
                  }}
                </span>
              </div>
            </div>

            <div class="follow-up-text">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.FOLLOW_UP.MSG') }}
            </div>
            <div
              :class="{
                'auto-generated-parent top-radius': true,
                'bottom-radius': followUp.auto,
                'text-gray': !followUp.auto,
              }"
            >
              <div>
                {{ $t('AGENT_BOTS.BOT_CONFIGURATION.FOLLOW_UP.AUTO') }}
              </div>
              <woot-switch
                size="medium"
                class="auto-offline--switch"
                :value="followUp.auto"
                @input="followUp.auto = !followUp.auto"
              />
            </div>

            <transition name="collapse">
              <div
                v-if="!followUp.auto"
                class="collapsible-content bottom-radius"
              >
                <div class="own-message">
                  <div class="own-message-content">
                    <p class="own-message-header">
                      {{
                        $t('AGENT_BOTS.BOT_CONFIGURATION.FOLLOW_UP.MSG_LABEL')
                      }}
                    </p>
                    <textarea
                      v-model="followUp.message"
                      rows="3"
                      type="text"
                      placeholder="Message"
                      class="red-error-border"
                      @input="onMessageInput()"
                    />
                    <span
                      v-if="$v.followUp.message.$error"
                      class="message follow-up-message"
                    >
                      {{
                        $t(
                          $v.followUp.message.maxLength
                            ? 'AGENT_BOTS.BOT_CONFIGURATION.FOLLOW_UP.MSG_ERROR'
                            : 'AGENT_BOTS.BOT_CONFIGURATION.FOLLOW_UP.MSG_ERROR_MAX'
                        )
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </transition>

            <div class="margin-top-2 button-container label-wrapper__buttons">
              <woot-submit-button
                button-text="Update"
                :disabled="!allowFollowUpUpdate"
                :loading="followUp.submitted"
              />
            </div>
          </div>
        </div>
      </settings-section>
    </form>
    <form v-if="isKB" @submit.prevent="updateAutoResolution">
      <settings-section
        v-if="!isACallInbox"
        :title="$t('AGENT_BOTS.BOT_CONFIGURATION.AUTO_RESOLUTION.TITLE')"
        :sub-title="$t('AGENT_BOTS.BOT_CONFIGURATION.AUTO_RESOLUTION.DESC')"
      >
        <div class="medium-8 columns">
          <div class="medium-6 padding-right-1">
            <select
              v-model="autoResolution.enabled"
              @change="autoResolutionChanged()"
            >
              <option :value="true">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.ENABLED'
                  )
                }}
              </option>
              <option :value="false">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.DISABLED'
                  )
                }}
              </option>
            </select>
          </div>
          <div v-if="autoResolution.enabled">
            <div class="row">
              <div class="columns medium-6 padding-right-1">
                <woot-input
                  v-model.number="autoResolution.mins"
                  type="number"
                  class="columns settings-item"
                  label="Mins"
                  placeholder="0"
                  @input="$v.autoResolution.mins.$touch"
                />
                <span
                  v-if="$v.autoResolution.mins.$error"
                  class="message follow-up-message"
                >
                  {{
                    $t('AGENT_BOTS.BOT_CONFIGURATION.FOLLOW_UP.VALID_DURATION')
                  }}
                </span>
              </div>
              <div class="columns medium-6 padding-left-1">
                <woot-input
                  v-model.number="autoResolution.secs"
                  type="number"
                  class="columns settings-item"
                  label="Seconds"
                  placeholder="0"
                  @input="$v.autoResolution.secs.$touch"
                />

                <span
                  v-if="$v.autoResolution.secs.$error"
                  class="message follow-up-message"
                >
                  {{
                    $t('AGENT_BOTS.BOT_CONFIGURATION.FOLLOW_UP.VALID_DURATION')
                  }}
                </span>
              </div>
            </div>
            <div class="follow-up-text">
              {{ $t('AGENT_BOTS.BOT_CONFIGURATION.AUTO_RESOLUTION.MSG') }}
            </div>
            <div class="margin-top-2 button-container label-wrapper__buttons">
              <woot-submit-button
                button-text="Update"
                :disabled="!allowAutoResolutionUpdate"
                :loading="autoResolution.submitted"
              />
            </div>
          </div>
        </div>
      </settings-section>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SettingsSection from 'dashboard/components/SettingsSection';
import alertMixin from 'shared/mixins/alertMixin';
import agentBotsApi from '../../../../../api/agentBots';
import inboxMixin, { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import {
  required,
  minValue,
  minLength,
  maxLength,
} from 'vuelidate/lib/validators';
import VFlag from 'dashboard/components/ui/VFlag';
const notBothZero = (minsField, secsField) => {
  return (_, vm) => {
    return vm[minsField] > 0 || vm[secsField] > 0;
  };
};
const notBothEmpty = (minsField, secsField) => {
  return (_, vm) => {
    return vm[minsField] !== '' || vm[secsField] !== '';
  };
};
import SelectTabDropdown from '../../../helpcenter/components/SelectTabDropdown.vue';

export default {
  components: {
    SelectTabDropdown,
    SettingsSection,
    VFlag,
  },
  mixins: [alertMixin, inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  validations() {
    return {
      followUp: {
        mins: {
          minValue: minValue(0),
          notBothZero: notBothZero('mins', 'secs'),
          notBothEmpty: notBothEmpty('mins', 'secs'),
        },
        secs: {
          minValue: minValue(0),
          notBothZero: notBothZero('mins', 'secs'),
          notBothEmpty: notBothEmpty('mins', 'secs'),
        },
        message: this.followUp.auto
          ? {}
          : {
              required,
              maxLength: maxLength(200),
              minLength: minLength(1),
            },
      },
      autoResolution: {
        mins: {
          minValue: minValue(0),
          notBothZero: notBothZero('mins', 'secs'),
          notBothEmpty: notBothEmpty('mins', 'secs'),
        },
        secs: {
          minValue: minValue(0),
          notBothZero: notBothZero('mins', 'secs'),
          notBothEmpty: notBothEmpty('mins', 'secs'),
        },
      },
    };
  },
  data() {
    return {
      selectedAgentBotId: null,
      followUp: {
        enabled: false,
        mins: 0,
        secs: 0,
        auto: true,
        message: '',
        submitted: false,
      },
      autoResolution: {
        enabled: false,
        mins: 0,
        secs: 0,
        submitted: false,
      },
      buttons: [
        { label: 'Rule-Based', type: 'rule' },
        { label: 'Knowledge-Based', type: 'knowledge' },
      ],
      botsOption: [],
      selectedBot: {},
      defaultButtonType: 'rule',
    };
  },
  computed: {
    ...mapGetters({
      agentBots: 'agentBots/getBots',
      uiFlags: 'agentBots/getUIFlags',
    }),
    isCallInbox() {
      return this.inbox.channel_type === 'Channel::Call';
    },
    allAgentBots() {
      if (
        this.inbox.channel_type !== INBOX_TYPES.WEB &&
        this.inbox.channel_type !== INBOX_TYPES.TELEGRAM &&
        this.inbox.channel_type !== INBOX_TYPES.YOAI
      )
        return this.agentBots.filter(
          bot => bot.description !== '___typebot___'
        );
      return this.agentBots;
    },
    getButtons() {
      const hasRuleBased = [
        INBOX_TYPES.WEB,
        INBOX_TYPES.TELEGRAM,
        INBOX_TYPES.YOAI,
      ].includes(this.inbox.channel_type);
      if (hasRuleBased) return this.buttons;
      return this.buttons.filter(item => item.type !== 'rule');
    },
    activeAgentBot() {
      return this.$store.getters['agentBots/getActiveAgentBot'](this.inbox.id);
    },
    isKB() {
      return (
        this.activeAgentBot?.description &&
        this.activeAgentBot.description !== '___typebot___'
      );
    },
    allowFollowUpUpdate() {
      if (this.$v.followUp.$invalid) {
        return false;
      }
      const hasTime = this.followUp.mins > 0 || this.followUp.secs > 0;
      if (hasTime) {
        return this.followUp.auto
          ? true
          : Boolean(this.followUp.message.trim().length);
      }
      return false;
    },
    allowAutoResolutionUpdate() {
      if (this.$v.autoResolution.$invalid) {
        return false;
      }
      return this.autoResolution.mins > 0 || this.autoResolution.secs > 0;
    },
    placeholderValue() {
      return this.selectedBot?.label
        ? this.selectedBot.label
        : this.$t('AGENT_BOTS.BOT_CONFIGURATION.TITLE');
    },
  },
  watch: {
    allAgentBots(newValue) {
      this.botsOption = newValue.map(bot => {
        return {
          ...bot,
          type: bot.description === '___typebot___' ? 'rule' : 'knowledge',
          label: bot.name,
        };
      });
    },
    activeAgentBot() {
      this.selectedAgentBotId = this.activeAgentBot.id;
      if (this.activeAgentBot?.id) {
        this.selectedBot = this.botsOption.find(
          i => i.id === this.activeAgentBot.id
        );
        this.defaultButtonType = this.selectedBot.type;
      } else {
        const resultType = this.botsOption.find(i => i.type === 'rule');
        this.defaultButtonType = resultType?.type
          ? resultType.type
          : 'knowledge';
      }
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
    this.$store.dispatch('agentBots/fetchAgentBotInbox', this.inbox.id);
    this.initFollowUp();
    this.initAutoResolution();
  },
  methods: {
    onMessageInput() {
      this.$v.followUp.message.$touch();
      this.$v.followUp.mins.$touch();
      this.$v.followUp.secs.$touch();
    },
    autoResolutionChanged() {
      if (!this.autoResolution.enabled) this.updateAutoResolution();
    },
    followUpChanged() {
      if (!this.followUp.enabled) this.updateFollowUp();
    },
    beforeLeave(el) {
      el.style.height = `${el.offsetHeight}px`;
    },
    leave(el, done) {
      const reflow = el.offsetHeight;
      el.style.transition = 'height 0.5s ease, opacity 0.5s ease';
      el.style.height = 0;
      el.style.opacity = 0;
      setTimeout(done, 500);
      return reflow;
    },
    changeType(type) {
      this.defaultButtonType = type;
    },
    async updateActiveAgentBot() {
      try {
        const canUpdate = await this.checkCanUpdate(this.selectedAgentBotId);
        if (!canUpdate) return;

        const botId = this.selectedAgentBotId
          ? this.selectedAgentBotId
          : undefined;

        await this.$store.dispatch('agentBots/setAgentBotInbox', {
          inboxId: this.inbox.id,
          // Added this to make sure that empty values are not sent to the API
          botId,
        });
        this.showAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.SUCCESS_MESSAGE'),
          'success'
        );
      } catch (error) {
        this.showAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.ERROR_MESSAGE'),
          'error'
        );
      }
    },
    async checkCanUpdate(botId) {
      const bot = this.agentBots.find(b => b.id === botId);
      if (bot?.description === '___typebot___') {
        const { data: typebot } = await agentBotsApi.getTypebot(botId);
        if (!typebot.published) {
          this.showAlert(
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.NOT_PUBLISHED_ERROR_MESSAGE'),
            'error'
          );
          return false;
        }
      }
      return true;
    },
    updateQueryParam() {
      const currentQuery = { ...this.$route.query };
      currentQuery.is_redirected = 0;
      // Build the new query string
      const newQueryString = Object.keys(currentQuery)
        .map(key => `${key}=${encodeURIComponent(currentQuery[key])}`)
        .join('&');
      const newUrl = `${this.$route.path}?${newQueryString}`;
      // updated the URL without redirecting
      window.history.replaceState(null, '', newUrl);
    },
    async redirectToCreateBot() {
      await this.$router.push({
        name: 'agent_bots',
        query: { is_redirected: 1 },
      });
      this.updateQueryParam();
    },
    handleSelectedBot(bot) {
      this.selectedBot = bot;
      this.selectedAgentBotId = bot.id;
    },
    async disconnectBot() {
      try {
        await this.$store.dispatch('agentBots/disconnectBot', {
          inboxId: this.inbox.id,
        });
        this.selectedBot = {};
        this.defaultButtonType = '';
        this.$store.dispatch('agentBots/get');
        this.showAlert(
          this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_SUCCESS_MESSAGE'),
          'success'
        );
      } catch (error) {
        this.showAlert(
          error?.message ||
            this.$t('AGENT_BOTS.BOT_CONFIGURATION.DISCONNECTED_ERROR_MESSAGE'),
          'error'
        );
      }
    },
    async updateFollowUp() {
      this.$v.followUp.$touch();
      if (this.$v.followUp.$invalid && this.followUp.enabled) {
        this.showAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'), 'error');
      } else {
        const payload = {
          id: this.inbox.id,
          formData: false,
          additional_attributes: {
            ...this.inbox.additional_attributes,
            kb_ai_follow_up_questions: {
              enabled: this.followUp.enabled,
              mins: +this.followUp.mins,
              secs: +this.followUp.secs,
              auto: this.followUp.auto,
              message: this.followUp.message,
            },
          },
        };
        this.followUp.submitted = true;
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.followUp.submitted = false;
        this.showAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'), 'success');
      }
    },
    async updateAutoResolution() {
      this.$v.autoResolution.$touch();
      if (this.$v.autoResolution.$invalid && this.autoResolution.enabled) {
        this.showAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'), 'error');
      } else {
        const payload = {
          id: this.inbox.id,
          formData: false,
          additional_attributes: {
            ...this.inbox.additional_attributes,
            ai_auto_resolution_of_inactive_conversations: {
              enabled: this.autoResolution.enabled,
              mins: +this.autoResolution.mins,
              secs: +this.autoResolution.secs,
            },
          },
        };
        this.autoResolution.submitted = true;
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.autoResolution.submitted = false;
        this.showAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'), 'success');
      }
    },
    initFollowUp() {
      const followUpData = {
        ...this.inbox.additional_attributes.kb_ai_follow_up_questions,
      };
      if (followUpData?.enabled) {
        this.followUp.enabled = followUpData.enabled;
        this.followUp.mins = followUpData.mins;
        this.followUp.secs = followUpData.secs;
        this.followUp.auto = followUpData.auto;
        this.followUp.message = followUpData.message;
      }
    },
    initAutoResolution() {
      const autoResolutionData = {
        ...this.inbox.additional_attributes
          .ai_auto_resolution_of_inactive_conversations,
      };
      if (autoResolutionData?.enabled) {
        this.autoResolution.enabled = autoResolutionData.enabled;
        this.autoResolution.mins = autoResolutionData.mins;
        this.autoResolution.secs = autoResolutionData.secs;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.no-assistant-wrapper {
  min-height: 160px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  .title {
    font-size: var(--font-size-medium);
    color: var(--d-800);
    font-weight: var(--font-weight-black);
  }
  .description {
    text-align: center;
    color: var(--d-500);
    font-size: var(--font-size-small);
  }
  .button {
    margin-top: 16px;
    padding: 10px 60px;
  }
}
.button--disconnect {
  margin-left: var(--space-small);
  &:hover {
    background: none !important;
  }
}
.assistants-list {
  padding: var(--space-one) var(--space-slab) var(--space-one) var(--space-slab);
  cursor: pointer;
  &:hover {
    background: #f7f7f9;
  }
}
.label-wrapper {
  min-width: fit-content;
  &__buttons {
    min-width: max-content;
  }
}
.auto-generated-parent {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #303030;
  background-color: #fafafa;
  font-size: var(--font-size-normal);
  font-weight: var(--font-weight-medium);
  padding: var(--space-slab);
}
.text-gray {
  color: #808080;
}
.follow-up-text {
  color: #808080;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-normal);
  margin-bottom: var(--space-slab);
}
.own-message {
  background-color: #fafafa;
  padding: var(--space-slab);
  .own-message-header {
    font-weight: var(--font-weight-medium);
    font-size: var(--font-size-normal);
  }
  .own-message-content {
    padding: var(--space-slab);
    background: #fff;
    border-radius: var(--border-radius-medium);
    border: 1px solid #d9d9d9;
  }
}
.top-radius {
  border-top-left-radius: var(--border-radius-medium);
  border-top-right-radius: var(--border-radius-medium);
}
.bottom-radius {
  border-bottom-left-radius: var(--border-radius-medium);
  border-bottom-right-radius: var(--border-radius-medium);
}
.collapsible-content {
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
}

.collapse-enter-active,
.collapse-leave-active {
  max-height: 500px;
}

.collapse-enter,
.collapse-leave-to {
  max-height: 0;
  padding: 0;
}
.follow-up-message {
  color: var(--r-600);
  display: block;
  margin: var(--space-minus-slab) 0 var(--space-slab);
  font-size: var(--font-size-mini);
}
.beta-notice {
  padding: var(--space-small) var(--space-snug);
  background-color: #f5f4fa;
  border-radius: var(--border-radius-normal);
  display: flex;
  align-items: center;
  justify-content: start;
  margin-bottom: var(--space-slab);
  .beta-notice-flag {
    height: 18px;
    width: 28px;
  }
  .beta-notice-title {
    font-weight: var(--font-weight-medium);
    font-size: var(--font-size-normal);
    color: var(--color-woot);
  }
  .beta-notice-desc {
    font-weight: var(--font-weight-normal);
    font-size: var(--font-size-normal);
    color: var(--d-500);
  }
}
</style>
